import { useCallback } from "react";

import { useTracking } from "react-tracking";

import {
  BuiDropdown,
  type IBuiDropdownProps,
} from "@btnet/blueprint-components/BuiDropdown";

export type IBTDropdownProps = IBuiDropdownProps;

/**
 * BTDropdown component - the core wrapper around the decorative BuiDropdown.
 *
 * Adds the cross-layer analytics seam: tracking is consumed from `react-tracking`
 * via React context (declared a peer dependency), never imported from the app's
 * analytics utility. This keeps `core-components` microfrontend-ready — the host
 * owns the tracking singleton.
 *
 * Note on `getPopupContainer`: it is a pure passthrough prop. The legacy wrapper
 * defaulted it to the app's `getClosestModal`, which is app code and cannot live in
 * this shared package. Consumers supply their own resolver (e.g. `getClosestModal`)
 * when they need popup containment within a modal/drawer.
 * @param props - The component props (same surface as BuiDropdown / antd DropDownProps).
 * @example
 * ```tsx
 * <BTDropdown
 *   testid="actions-dropdown"
 *   overlay={<BTMenu testid="actions-menu">...</BTMenu>}
 * >
 *   <BTButton data-testid="trigger">Actions</BTButton>
 * </BTDropdown>
 * ```
 */
export const BTDropdown = (props: IBTDropdownProps) => {
  const { onVisibleChange, testid } = props;
  const tracking = useTracking<Record<string, unknown>>({
    element: "Dropdown Menu",
    uniqueId: testid,
  });

  const handleVisibleChange = useCallback(
    (visible: boolean) => {
      // There's no 'CloseMenu' variant so we just track open.
      if (visible) {
        tracking.trackEvent({ event: "OpenMenu" });
      }
      onVisibleChange?.(visible);
    },
    [tracking, onVisibleChange],
  );

  return <BuiDropdown {...props} onVisibleChange={handleVisibleChange} />;
};
